import React, { Fragment } from 'react';
import axios from 'axios';
import {
    Form,
    Checkbox,
    Header,
    Loader,
    Segment,
    Table,
    Button,
    Icon,
    Popup
} from 'semantic-ui-react';
import { fetchStudyCycle } from '../../Lookups/util';
import ReactExport from 'react-data-export';
import ReportExcelFile from './ReportExcelFile';
import {
    additionalFilterSectionStyle,
    filterFieldGridStyle,
    requiredFilterSectionStyle
} from './reportUiHelpers';

const ExcelFile = ReportExcelFile;
const { ExcelSheet } = ReactExport.ExcelFile;

const studyTypeColumnStyle = {
    width: '44px',
    minWidth: '44px',
    maxWidth: '52px',
    paddingLeft: '4px',
    paddingRight: '4px',
};

const activeStudentsBorderStyle = {
    borderLeft: '1px solid rgba(34, 36, 38, 0.15)',
};

class StudentsEligibleForNextStudyYear extends React.Component {
    state = {
        semesters: [],
        selectedSemesterId: null,
        selectedOrganizationalUnitId: null,
        studyCycles: [],
        selectedStudyCycleId: null,
        studyProgramBasics: [],
        allStudyProgramBasics: [],
        selectedStudyProgramBasicId: null,
        selectedStudyYear: null,
        reportData: [],
        loaded: false,
        reportRequested: false,
        includeDetails: false,
    };

    componentDidMount() {
        const centralEducationOffice = this.isCentralEducationOffice();
        const studyProgramRequest = centralEducationOffice
            ? Promise.resolve({ data: [] })
            : axios.post('/study-programs/basic', {});

        axios.all([
            axios.get('/semester/enrollment/'),
            fetchStudyCycle(),
            studyProgramRequest
        ]).then(axios.spread((semesterResponse, cycleResponse, studyProgramResponse) => {
            const futureWinterSemesters = semesterResponse.data
                .filter(semester =>
                    semester.name &&
                    semester.name._key === 'winter semester' &&
                    semester.startDate &&
                    new Date(semester.startDate).getTime() > Date.now()
                )
                .sort((first, second) =>
                    first.academicYear.startYear - second.academicYear.startYear
                );

            const semesters = futureWinterSemesters.length
                ? [futureWinterSemesters[0]]
                : [];

            const selectedSemesterId = semesters.length
                ? semesters[0].id
                : null;

            const studyCycles = cycleResponse.data.map(cycle => ({
                ...cycle,
                key: cycle.id,
                value: cycle.id,
                text: cycle.displayName
            }));

            const studyProgramBasics = studyProgramResponse.data
                .filter(item => !item.studyProgramBasic.archived)
                .map(item => ({
                    ...item,
                    key: item.studyProgramBasic.id,
                    value: item.studyProgramBasic.id,
                    text: item.studyProgramBasic.name,
                    description: item.studyProgramBasic.comment
                        ? <span style={{ color: '#777' }}>{item.studyProgramBasic.comment}</span>
                        : undefined
                }));

            this.setState({
                semesters,
                selectedSemesterId,
                studyCycles,
                studyProgramBasics,
                allStudyProgramBasics: studyProgramBasics
            });
        }))
            .catch(() => this.setState({ loaded: false }));
    }

    isCentralEducationOffice = () => {
        const activeRole = localStorage.getItem('activeRole') || '';
        return activeRole.substr(0, 38) === 'Služba za nastavu i studentska pitanja';
    };

    loadStudyProgramBasics = organizationalUnitId => {
        if (!organizationalUnitId) {
            this.setState({
                studyProgramBasics: [],
                allStudyProgramBasics: [],
                selectedStudyProgramBasicId: null
            });
            return;
        }

        axios.post(`/study-programs/basic/${organizationalUnitId}`, {})
            .then(response => {
                const studyProgramBasics = response.data
                    .filter(item => !item.studyProgramBasic.archived)
                    .map(item => ({
                        ...item,
                        key: item.studyProgramBasic.id,
                        value: item.studyProgramBasic.id,
                        text: item.studyProgramBasic.name,
                        description: item.studyProgramBasic.comment
                            ? <span style={{ color: '#777' }}>{item.studyProgramBasic.comment}</span>
                            : undefined
                    }));

                this.setState({
                    studyProgramBasics,
                    allStudyProgramBasics: studyProgramBasics,
                    selectedStudyProgramBasicId: null
                });
            })
            .catch(() => this.setState({
                studyProgramBasics: [],
                allStudyProgramBasics: [],
                selectedStudyProgramBasicId: null
            }));
    };

    loadStudentCount = () => {
        const {
            selectedSemesterId,
            selectedOrganizationalUnitId,
            selectedStudyCycleId,
            selectedStudyProgramBasicId,
            selectedStudyYear
        } = this.state;

        if (
            !selectedSemesterId ||
            (this.isCentralEducationOffice() && !selectedOrganizationalUnitId)
        ) return;

        this.setState({
            loaded: true,
            reportRequested: true,
            reportData: []
        });

        axios.get(
            `/semester-enrollment/eligible-for-next-study-year/${selectedSemesterId}`,
            {
                params: {
                    organizationalUnitId: selectedOrganizationalUnitId,
                    studyCycleId: selectedStudyCycleId,
                    studyProgramBasicId: selectedStudyProgramBasicId,
                    studyYear: selectedStudyYear,
                    includeDetails: this.state.includeDetails
                }
            }
        )
            .then(response => {
                this.setState({
                    reportData: response.data,
                    loaded: false
                });
            })
            .catch(() => {
                this.setState({
                    reportData: [],
                    loaded: false
                });
            });
    };

    handleSemesterChange = (event, { value }) => {
        this.setState({
            selectedSemesterId: value,
            reportData: []
        });
    };

    handleOrganizationalUnitChange = (event, { value }) => {
        this.setState({
            selectedOrganizationalUnitId: value || null,
            selectedStudyCycleId: null,
            selectedStudyProgramBasicId: null,
            selectedStudyYear: null,
            reportData: [],
            reportRequested: false
        });

        this.loadStudyProgramBasics(value);
    };

    handleAdditionalFilterChange = (event, { name, value }) => {
        if (name === 'selectedStudyCycleId') {
            const studyProgramBasics = value
                ? this.state.allStudyProgramBasics.filter(item =>
                    item.studyProgramBasic.studyCycles &&
                    item.studyProgramBasic.studyCycles.some(
                        cycle => cycle.id === value
                    )
                )
                : this.state.allStudyProgramBasics;

            this.setState({
                selectedStudyCycleId: value || null,
                selectedStudyProgramBasicId: null,
                studyProgramBasics,
                reportData: []
            });

            return;
        }

        this.setState({
            [name]: value || null,
            reportData: []
        });
    };

    render() {
        const centralEducationOffice = this.isCentralEducationOffice();
        const detailColumns = [
            ['studyCycleName', 'Ciklus'], ['studyProgramName', 'Studijski program'],
            ['studyYear', 'Trenutna godina'], ['index', 'Broj indeksa'], ['lastName', 'Prezime'], ['firstName', 'Ime'],
            ['studyType', 'Tip studiranja'], ['eligibility', 'Uslov za']
        ];
        const detailColumnStyle = key => (key === 'studyYear' || key === 'studyType'
            ? { width: '75px', minWidth: '65px', whiteSpace: 'normal', textAlign: 'center' }
            : {});
        const detailRows = this.state.reportData.reduce((rows, group) => rows.concat(
            (group.students || []).map(student => ({
                ...student, studyCycleName: group.studyCycleName,
                studyProgramName: group.studyProgramName, studyYear: group.studyYear
            }))
        ), []);
        const detailDataSet = [{
            columns: detailColumns.map(column => ({
                title: column[1],
                width: { wpx: column[0] === 'studyYear' || column[0] === 'studyType' ? 75 : 180 }
            })),
            data: detailRows.map(student => detailColumns.map(column => ({ value: student[column[0]] || '—' })))
        }];

        const semesterOptions = this.state.semesters.map(semester => ({
            key: semester.id,
            value: semester.id,
            text: semester.academicYear.name,
        }));

        const studyYearOptions = [1, 2, 3, 4, 5, 6].map(year => ({
            key: year,
            value: year,
            text: `${year}. godina`
        }));

        const dataByCycle = this.state.reportData.reduce((cycles, row) => {
            if (!cycles[row.studyCycleId]) {
                cycles[row.studyCycleId] = {
                    name: row.studyCycleName,
                    rows: []
                };
            }

            cycles[row.studyCycleId].rows.push(row);

            return cycles;
        }, {});

        /*
         * EXCEL PODACI
         */
        const excelData = this.state.reportData.map(row => ([
            {
                value: row.studyCycleName || ''
            },
            {
                value: row.studyProgramName || ''
            },
            {
                value: row.studyYear
                    ? `${row.studyYear}. godina${row.finalStudyYear
                        ? ' (završna godina)'
                        : ''}`
                    : ''
            },

            // Aktivni studenti
            {
                value: row.regularStudentCount || 0
            },
            {
                value: row.selfFinancingStudentCount || 0
            },
            {
                value: row.partTimeStudentCount || 0
            },
            {
                value: row.distanceLearningStudentCount || 0
            },
            {
                value: row.activeStudentCount || 0
            },

            // Godina na koju trebaju preći
            {
                value: row.finalStudyYear || !row.studyYear
                    ? '-'
                    : `${row.studyYear + 1}. godina`
            },

            // Studenti koji imaju uslov
            {
                value: row.finalStudyYear || !row.studyYear
                    ? '-'
                    : row.eligibleRegularStudentCount
            },
            {
                value: row.finalStudyYear || !row.studyYear
                    ? '-'
                    : row.eligibleSelfFinancingStudentCount
            },
            {
                value: row.finalStudyYear || !row.studyYear
                    ? '-'
                    : row.eligiblePartTimeStudentCount
            },
            {
                value: row.finalStudyYear || !row.studyYear
                    ? '-'
                    : row.eligibleDistanceLearningStudentCount
            },
            {
                value: row.finalStudyYear || !row.studyYear
                    ? '-'
                    : row.eligibleStudentCount
            },

            // Završna godina
            {
                value: row.finalStudyYear
                    ? row.graduatingStudentCount
                    : '-'
            },
            {
                value: row.finalStudyYear
                    ? row.potentialGradStudentCount
                    : '-'
            }
        ]));

        const excelDataSet = [
            {
                columns: [
                    {
                        title: 'Ciklus studija',
                        width: { wpx: 180 }
                    },
                    {
                        title: 'Studijski program',
                        width: { wpx: 300 }
                    },
                    {
                        title: 'Trenutna godina studija',
                        width: { wpx: 180 }
                    },
                    {
                        title: 'Aktivni studenti - R',
                        width: { wpx: 120 }
                    },
                    {
                        title: 'Aktivni studenti - RSF',
                        width: { wpx: 130 }
                    },
                    {
                        title: 'Aktivni studenti - V',
                        width: { wpx: 120 }
                    },
                    {
                        title: 'Aktivni studenti - DL',
                        width: { wpx: 120 }
                    },
                    {
                        title: 'Ukupno aktivnih studenata',
                        width: { wpx: 170 }
                    },
                    {
                        title: 'Godina na koju trebaju preći',
                        width: { wpx: 190 }
                    },
                    {
                        title: 'Uslov za prelazak - R',
                        width: { wpx: 140 }
                    },
                    {
                        title: 'Uslov za prelazak - RSF',
                        width: { wpx: 150 }
                    },
                    {
                        title: 'Uslov za prelazak - V',
                        width: { wpx: 140 }
                    },
                    {
                        title: 'Uslov za prelazak - DL',
                        width: { wpx: 140 }
                    },
                    {
                        title: 'Ukupno studenata sa uslovom',
                        width: { wpx: 190 }
                    },
                    {
                        title: 'Diplomiranje',
                        width: { wpx: 130 }
                    },
                    {
                        title: 'Apsolventski staž / obnova apsolventskog staža',
                        width: { wpx: 260 }
                    }
                ],
                data: excelData
            }
        ];

        return (
            <div>
                <Form>
                    <Segment style={requiredFilterSectionStyle}>
                        <Header as="h4" color="blue">
                            Obavezni parametri
                        </Header>

                        <Form.Group widths="equal">
                            {centralEducationOffice && (
                                <Form.Field required>
                                    <Form.Dropdown
                                        label="Organizaciona jedinica"
                                        placeholder="Odaberite organizacionu jedinicu"
                                        name="selectedOrganizationalUnitId"
                                        options={this.props.organizationalUnits}
                                        value={
                                            this.state.selectedOrganizationalUnitId
                                        }
                                        onChange={
                                            this.handleOrganizationalUnitChange
                                        }
                                        search
                                        fluid
                                        selection
                                    />
                                </Form.Field>
                            )}

                            <Form.Field>
                                <Form.Dropdown
                                    label="Akademska godina u koju se studenti trebaju upisati"
                                    placeholder="Odaberite akademsku godinu"
                                    name="selectedSemesterId"
                                    options={semesterOptions}
                                    value={this.state.selectedSemesterId}
                                    onChange={this.handleSemesterChange}
                                    search
                                    fluid
                                    selection
                                />
                            </Form.Field>

                            {!centralEducationOffice && <Form.Field />}

                            <Form.Field />
                        </Form.Group>
                    </Segment>
                </Form>

                <Form>
                    <Segment style={additionalFilterSectionStyle}>
                        <Header as="h4" color="blue">
                            Dodatni filteri
                        </Header>

                        <Form.Group
                            widths="equal"
                            style={filterFieldGridStyle}
                        >
                            <Form.Field>
                                <Form.Dropdown
                                    label="Ciklus studija"
                                    placeholder="Odaberite ciklus"
                                    name="selectedStudyCycleId"
                                    options={this.state.studyCycles}
                                    value={
                                        this.state.selectedStudyCycleId
                                    }
                                    onChange={
                                        this.handleAdditionalFilterChange
                                    }
                                    clearable
                                    search
                                    fluid
                                    selection
                                />
                            </Form.Field>

                            <Form.Field>
                                <Form.Dropdown
                                    label="Studijski program"
                                    placeholder="Odaberite studijski program"
                                    name="selectedStudyProgramBasicId"
                                    options={
                                        this.state.studyProgramBasics
                                    }
                                    value={
                                        this.state
                                            .selectedStudyProgramBasicId
                                    }
                                    onChange={
                                        this.handleAdditionalFilterChange
                                    }
                                    clearable
                                    search
                                    fluid
                                    selection
                                />
                            </Form.Field>

                            <Form.Field>
                                <Form.Dropdown
                                    label="Godina studija"
                                    placeholder="Odaberite godinu studija"
                                    name="selectedStudyYear"
                                    options={studyYearOptions}
                                    value={
                                        this.state.selectedStudyYear
                                    }
                                    onChange={
                                        this.handleAdditionalFilterChange
                                    }
                                    clearable
                                    search
                                    fluid
                                    selection
                                />
                            </Form.Field>
                        </Form.Group>
                    <Form.Field style={{ marginTop: '12px', marginBottom: '16px' }}>
                        <Checkbox
                            label="Prikaži detaljne podatke o studentima"
                            checked={this.state.includeDetails}
                            disabled={this.state.loaded}
                            onChange={(event, { checked }) => this.setState({
                                includeDetails: checked, reportData: [], reportRequested: false
                            })}
                        />
                    </Form.Field>
                    </Segment>
                    <Button
                        color="blue"
                        floated="right"
                        disabled={
                            !this.state.selectedSemesterId ||
                            (
                                centralEducationOffice &&
                                !this.state.selectedOrganizationalUnitId
                            ) ||
                            this.state.loaded
                        }
                        onClick={this.loadStudentCount}
                    >
                        Prikaži izvještaj
                    </Button>
                </Form>

                <br />
                <br />
                <br />

                {this.state.reportData.length > 0 && (
                    <div
                        style={{
                            textAlign: 'right',
                            marginBottom: '10px'
                        }}
                    >
                        <ExcelFile
                            filename="Studenti sa uslovom za narednu godinu"
                            element={
                                <Popup
                                    trigger={
                                        <Button
                                            animated="vertical"
                                            color="green"
                                            basic
                                        >
                                            <Button.Content hidden>
                                                <Icon name="download" />
                                            </Button.Content>

                                            <Button.Content visible>
                                                Excel
                                            </Button.Content>
                                        </Button>
                                    }
                                    content="Preuzmi izvještaj u Excel-u"
                                    hideOnScroll
                                    position="top center"
                                />
                            }
                        >
                            <ExcelSheet
                                dataSet={excelDataSet}
                                name="Studenti"
                            />
                        </ExcelFile>
                    </div>
                )}

                {this.state.loaded ? (
                    <Loader active inline="centered">
                        Učitavanje podataka može potrajati, molimo da
                        sačekate.
                    </Loader>
                ) : this.state.reportData.length ? (
                    <Table celled compact>
                        <Table.Header>
                            <Table.Row>
                                <Table.HeaderCell rowSpan="3">
                                    Ciklus studija
                                </Table.HeaderCell>

                                <Table.HeaderCell rowSpan="3">
                                    Studijski program
                                </Table.HeaderCell>

                                <Table.HeaderCell
                                    rowSpan="3"
                                    style={{
                                        borderRight:
                                            '1px solid rgba(34, 36, 38, 0.15)'
                                    }}
                                >
                                    Trenutna godina studija
                                </Table.HeaderCell>

                                <Table.HeaderCell
                                    colSpan="4"
                                    textAlign="center"
                                    style={activeStudentsBorderStyle}
                                >
                                    Broj aktivnih studenata na trenutnoj
                                    godini studija
                                </Table.HeaderCell>

                                <Table.HeaderCell
                                    rowSpan="3"
                                    style={{
                                        borderRight:
                                            '1px solid rgba(34, 36, 38, 0.15)'
                                    }}
                                >
                                    Godina na koju trebaju preći
                                </Table.HeaderCell>

                                <Table.HeaderCell
                                    colSpan="6"
                                    textAlign="center"
                                >
                                    Studenti koji imaju uslov za:
                                </Table.HeaderCell>
                            </Table.Row>

                            <Table.Row>
                                <Table.HeaderCell
                                    rowSpan="2"
                                    textAlign="center"
                                    style={{
                                        ...studyTypeColumnStyle,
                                        ...activeStudentsBorderStyle
                                    }}
                                >
                                    R
                                </Table.HeaderCell>

                                <Table.HeaderCell
                                    rowSpan="2"
                                    textAlign="center"
                                    style={studyTypeColumnStyle}
                                >
                                    RSF
                                </Table.HeaderCell>

                                <Table.HeaderCell
                                    rowSpan="2"
                                    textAlign="center"
                                    style={studyTypeColumnStyle}
                                >
                                    V
                                </Table.HeaderCell>

                                <Table.HeaderCell
                                    rowSpan="2"
                                    textAlign="center"
                                    style={studyTypeColumnStyle}
                                >
                                    DL
                                </Table.HeaderCell>

                                <Table.HeaderCell
                                    colSpan="4"
                                    textAlign="center"
                                    style={{
                                        borderLeft:
                                            '1px solid rgba(34, 36, 38, 0.15)'
                                    }}
                                >
                                    Prelazak na narednu godinu
                                </Table.HeaderCell>

                                <Table.HeaderCell
                                    rowSpan="2"
                                    textAlign="center"
                                >
                                    Diplomiranje
                                </Table.HeaderCell>

                                <Table.HeaderCell
                                    rowSpan="2"
                                    textAlign="center"
                                >
                                    Upis u apsolventski staž / obnovu
                                    apsolventskog staža
                                </Table.HeaderCell>
                            </Table.Row>

                            <Table.Row>
                                <Table.HeaderCell
                                    textAlign="center"
                                    style={{
                                        ...studyTypeColumnStyle,
                                        ...activeStudentsBorderStyle
                                    }}
                                >
                                    R
                                </Table.HeaderCell>

                                <Table.HeaderCell
                                    textAlign="center"
                                    style={studyTypeColumnStyle}
                                >
                                    RSF
                                </Table.HeaderCell>

                                <Table.HeaderCell
                                    textAlign="center"
                                    style={studyTypeColumnStyle}
                                >
                                    V
                                </Table.HeaderCell>

                                <Table.HeaderCell
                                    textAlign="center"
                                    style={studyTypeColumnStyle}
                                >
                                    DL
                                </Table.HeaderCell>
                            </Table.Row>
                        </Table.Header>

                        <Table.Body>
                            {Object.keys(dataByCycle).map(cycleId => {
                                const cycle = dataByCycle[cycleId];

                                const activeTotal = cycle.rows.reduce(
                                    (total, row) =>
                                        total + row.activeStudentCount,
                                    0
                                );

                                const eligibleTotal = cycle.rows.reduce(
                                    (total, row) =>
                                        total + row.eligibleStudentCount,
                                    0
                                );

                                const graduatingTotal =
                                    cycle.rows.reduce(
                                        (total, row) =>
                                            total +
                                            row.graduatingStudentCount,
                                        0
                                    );

                                const potentialGradTotal =
                                    cycle.rows.reduce(
                                        (total, row) =>
                                            total +
                                            row.potentialGradStudentCount,
                                        0
                                    );

                                return (
                                    <Fragment key={cycleId}>
                                        {cycle.rows.map(
                                            (row, index) => (
                                                <Fragment
                                                    key={
                                                        `${row.studyCycleId}-` +
                                                        `${row.studyProgramBasicId}-` +
                                                        `${row.studyYear || 0}-` +
                                                        `${row.finalStudyYear
                                                            ? 'final'
                                                            : 'continuing'}`
                                                    }
                                                >
                                                    <Table.Row>
                                                        {index === 0 && (
                                                            <Table.Cell
                                                                rowSpan={
                                                                    cycle.rows
                                                                        .length *
                                                                    2
                                                                }
                                                                verticalAlign="middle"
                                                                style={{
                                                                    borderRight:
                                                                        '1px solid rgba(34, 36, 38, 0.1)'
                                                                }}
                                                            >
                                                                {
                                                                    cycle.name
                                                                }
                                                            </Table.Cell>
                                                        )}

                                                        <Table.Cell
                                                            rowSpan="2"
                                                            verticalAlign="middle"
                                                        >
                                                            {
                                                                row.studyProgramName
                                                            }
                                                        </Table.Cell>

                                                        <Table.Cell
                                                            rowSpan="2"
                                                            verticalAlign="middle"
                                                            style={{
                                                                borderRight:
                                                                    '1px solid rgba(34, 36, 38, 0.15)'
                                                            }}
                                                        >
                                                            {row.studyYear
                                                                ? `${row.studyYear}. godina${row.finalStudyYear
                                                                    ? ' (završna godina)'
                                                                    : ''}`
                                                                : '-'}
                                                        </Table.Cell>

                                                        <Table.Cell
                                                            textAlign="center"
                                                            style={{
                                                                ...studyTypeColumnStyle,
                                                                ...activeStudentsBorderStyle
                                                            }}
                                                        >
                                                            {
                                                                row.regularStudentCount
                                                            }
                                                        </Table.Cell>

                                                        <Table.Cell
                                                            textAlign="center"
                                                            style={
                                                                studyTypeColumnStyle
                                                            }
                                                        >
                                                            {
                                                                row.selfFinancingStudentCount
                                                            }
                                                        </Table.Cell>

                                                        <Table.Cell
                                                            textAlign="center"
                                                            style={
                                                                studyTypeColumnStyle
                                                            }
                                                        >
                                                            {
                                                                row.partTimeStudentCount
                                                            }
                                                        </Table.Cell>

                                                        <Table.Cell
                                                            textAlign="center"
                                                            style={
                                                                studyTypeColumnStyle
                                                            }
                                                        >
                                                            {
                                                                row.distanceLearningStudentCount
                                                            }
                                                        </Table.Cell>

                                                        <Table.Cell
                                                            rowSpan="2"
                                                            verticalAlign="middle"
                                                            style={{
                                                                borderRight:
                                                                    '1px solid rgba(34, 36, 38, 0.15)'
                                                            }}
                                                        >
                                                            {row.finalStudyYear ||
                                                            !row.studyYear
                                                                ? '-'
                                                                : `${row.studyYear + 1}. godina`}
                                                        </Table.Cell>

                                                        <Table.Cell
                                                            textAlign="center"
                                                            style={{
                                                                ...studyTypeColumnStyle,
                                                                ...activeStudentsBorderStyle
                                                            }}
                                                        >
                                                            {row.finalStudyYear ||
                                                            !row.studyYear
                                                                ? '-'
                                                                : row.eligibleRegularStudentCount}
                                                        </Table.Cell>

                                                        <Table.Cell
                                                            textAlign="center"
                                                            style={
                                                                studyTypeColumnStyle
                                                            }
                                                        >
                                                            {row.finalStudyYear ||
                                                            !row.studyYear
                                                                ? '-'
                                                                : row.eligibleSelfFinancingStudentCount}
                                                        </Table.Cell>

                                                        <Table.Cell
                                                            textAlign="center"
                                                            style={
                                                                studyTypeColumnStyle
                                                            }
                                                        >
                                                            {row.finalStudyYear ||
                                                            !row.studyYear
                                                                ? '-'
                                                                : row.eligiblePartTimeStudentCount}
                                                        </Table.Cell>

                                                        <Table.Cell
                                                            textAlign="center"
                                                            style={
                                                                studyTypeColumnStyle
                                                            }
                                                        >
                                                            {row.finalStudyYear ||
                                                            !row.studyYear
                                                                ? '-'
                                                                : row.eligibleDistanceLearningStudentCount}
                                                        </Table.Cell>

                                                        <Table.Cell
                                                            textAlign="center"
                                                            rowSpan="2"
                                                            verticalAlign="middle"
                                                        >
                                                            {row.finalStudyYear
                                                                ? row.graduatingStudentCount
                                                                : '-'}
                                                        </Table.Cell>

                                                        <Table.Cell
                                                            textAlign="center"
                                                            rowSpan="2"
                                                            verticalAlign="middle"
                                                        >
                                                            {row.finalStudyYear
                                                                ? row.potentialGradStudentCount
                                                                : '-'}
                                                        </Table.Cell>
                                                    </Table.Row>

                                                    <Table.Row>
                                                        <Table.Cell
                                                            colSpan="4"
                                                            textAlign="center"
                                                            style={
                                                                activeStudentsBorderStyle
                                                            }
                                                        >
                                                            <strong>
                                                                Ukupno:{' '}
                                                                {
                                                                    row.activeStudentCount
                                                                }
                                                            </strong>
                                                        </Table.Cell>

                                                        <Table.Cell
                                                            colSpan="4"
                                                            textAlign="center"
                                                            style={
                                                                activeStudentsBorderStyle
                                                            }
                                                        >
                                                            <strong>
                                                                {row.finalStudyYear ||
                                                                !row.studyYear
                                                                    ? 'Ukupno: -'
                                                                    : `Ukupno: ${row.eligibleStudentCount}`}
                                                            </strong>
                                                        </Table.Cell>
                                                    </Table.Row>
                                                </Fragment>
                                            )
                                        )}

                                        <Table.Row active>
                                            <Table.Cell colSpan="3">
                                                <strong>
                                                    Ukupno za {cycle.name}
                                                </strong>
                                            </Table.Cell>

                                            <Table.Cell
                                                colSpan="4"
                                                textAlign="center"
                                                style={
                                                    activeStudentsBorderStyle
                                                }
                                            >
                                                <strong>
                                                    {activeTotal}
                                                </strong>
                                            </Table.Cell>

                                            <Table.Cell
                                                style={{
                                                    borderRight:
                                                        '1px solid rgba(34, 36, 38, 0.15)'
                                                }}
                                            >
                                                -
                                            </Table.Cell>

                                            <Table.Cell
                                                colSpan="4"
                                                textAlign="center"
                                                style={{
                                                    borderLeft:
                                                        '1px solid rgba(34, 36, 38, 0.15)'
                                                }}
                                            >
                                                <strong>
                                                    {eligibleTotal}
                                                </strong>
                                            </Table.Cell>

                                            <Table.Cell textAlign="center">
                                                <strong>
                                                    {graduatingTotal}
                                                </strong>
                                            </Table.Cell>

                                            <Table.Cell textAlign="center">
                                                <strong>
                                                    {potentialGradTotal}
                                                </strong>
                                            </Table.Cell>
                                        </Table.Row>
                                    </Fragment>
                                );
                            })}
                        </Table.Body>
                    </Table>
                ) : this.state.reportRequested ? (
                    <Segment>
                        Nema dostupnih studijskih programa za izabrane
                        parametre.
                    </Segment>
                ) : null}
                {this.state.includeDetails && !this.state.loaded && this.state.reportData.length > 0 && (
                    <Segment>
                        <Header as="h3">Detaljni podaci o studentima</Header>
                        <p>Spisak odgovara brojevima za prelazak u narednu godinu, diplomiranje i potencijalni upis u apsolventski staž ili njegovu obnovu iz gornje tabele. Ne uključuje ostale aktivne studente. Uslovi se računaju istim provjerama kao u zbirnom prikazu.</p>
                        <ExcelFile filename="Studenti sa uslovom - detaljni prikaz"
                            element={<Button color="green" basic>Excel</Button>}>
                            <ExcelSheet dataSet={detailDataSet} name="Detalji" />
                        </ExcelFile>
                        {detailRows.length ? <Table celled compact>
                            <Table.Header><Table.Row>
                                {detailColumns.map(column => <Table.HeaderCell key={column[0]} style={detailColumnStyle(column[0])}>{column[1]}</Table.HeaderCell>)}
                            </Table.Row></Table.Header>
                            <Table.Body>{detailRows.map((student, index) => <Table.Row key={index}>
                                {detailColumns.map(column => <Table.Cell key={column[0]} style={detailColumnStyle(column[0])}>{student[column[0]] || '—'}</Table.Cell>)}
                            </Table.Row>)}</Table.Body>
                        </Table> : <p>Nema studenata za detaljni prikaz.</p>}
                    </Segment>
                )}
            </div>
        );
    }
}

export default StudentsEligibleForNextStudyYear;



// WEBPACK FOOTER //
// ./src/views/StudentService/StudentsReports/StudentsEligibleForNextStudyYear.js